import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Dialog, makeStyles, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import ReactECharts from 'echarts-for-react';
import html2canvas from 'html2canvas';
import _, { isArray } from 'lodash';
import { CloseIcon, DownloadIcon } from '../../../../components/Icon';
import { EQUIPMENT_PIE_STATUS_ENUM } from '../../../../comm/constant';
import StatusCom from '../../../../components/StatusCom';
import { i18n } from '../../../../language';
import { equipmentAPI } from '../../../../apis';
import TableUI from '../../../../components/TableUI';
import { downloadFileById } from '../../../../utils';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: 'none',
    },
    '& .MuiPaper-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '0.9375rem',
      boxShadow: '0 -0.125rem 1.3125rem rgba(30, 56, 140, 0.18)',
      margin: '0',
      width: '67rem',
      height: '53.313rem',
      paddingTop: '1.625rem',
      display: 'flex',
      overflowY: 'auto',
      flexDirection: 'column',
    },
  },
  h1: {
    fontSize: '1.5rem',
    fontFamily: 'NotoSans-Bold',
    color: '#2e4469',
  },
  h2: {
    fontSize: '1.125rem',
    fontFamily: 'NotoSans-Bold',
    color: '#2e4469',
  },
  closeBtn: {
    width: '1.5rem',
    height: '1.5rem',
  },
  closeIcon: {
    fontSize: '1rem',
    color: '#538fff',
  },
  totalEquipment: {
    marginLeft: '0.5rem',
    fontFamily: 'NotoSans-Bold',
    fontSize: '2rem',
    color: '#538fff',
  },
  pieChartBox: {
    width: '31.25rem',
    height: '40.375rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    padding: '1rem',
  },
  statusPieChart: {
    height: '27.2rem !important',
  },
  locationPieChart: {
    height: '16.8rem !important',
    width: '16.8rem !important',
    transform: 'translate(0, -10px)',
  },
  statusGrid: {
    display: 'grid',
    // gridAutoFlow: 'column',
    gridTemplateRows: '1fr 1fr 1fr',
    gridRowGap: '0.8rem',
    marginLeft: '1.25rem',
    gridTemplateColumns: 'auto auto auto',
  },
  locationRemark: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#7d86a9',
  },
  text: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#7d86a9',
  },
  containerClassName: {
    padding: '0 0.813rem 0 0.625rem',
    borderRadius: '8px',
    border: 'solid 1px #c7c7c7',
    backgroundColor: '#f1f6ff',
    transform: 'translateY(-10px)',
  },
  tableClassName: {
    position: 'relative',
    height: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0.5rem',
    '& .MuiTableCell-root': {
      border: 'none',
      fontSize: '1rem',
      fontWeight: '500',
      lineHeight: '1.675rem',
      color: '#2e4469',
      padding: '0 1rem ',
    },
    '& .MuiTableHead-root': {
      position: 'sticky',
      top: '0',
      left: '0',
      zIndex: '10',
      '& .MuiTableRow-root': {
        backgroundColor: '#f1f6ff',
      },
      '& .MuiTableCell-root': {
        fontWeight: 'bold',
        color: '#538fff',
        padding: '0 0.625rem',
        backgroundColor: '#f1f6ff',
        fontSize: '0.875rem',
        fontFamily: 'NotoSans-Bold',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        backgroundColor: '#fff',
      },
      '& .MuiTableCell-root': {
        padding: '0.563rem 0rem',
        fontFamily: 'NotoSans-Medium',
        '&:first-child': {
          borderTopLeftRadius: '8px',
          borderBottomLeftRadius: '8px',
          paddingLeft: '0.625rem',
        },
        '&:last-child': {
          borderTopRightRadius: '8px',
          borderBottomRightRadius: '8px',
        },
      },
    },
  },
  downloadButton: {
    padding: '0 0.688rem',
    height: '3rem',
    backgroundColor: '#538fff',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#fff',
  },
  downloadIcon: {
    fontSize: '1.5rem',
    color: '#fff',
    marginRight: '0.25rem',
  },
  category: {
    padding: '0.563rem 1rem',
    borderRadius: '22px',
    backgroundColor: '#f1f6ff',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridColumnGap: '0.5rem',
  },
  categoryText: {
    color: '#2e4469',
  },
  categoryNum: {
    color: '#538fff',
  },
}));

const CategoryLabel = ({ text, num }) => {
  const classes = useStyles();

  return (
    <div className={classes.category}>
      <div className={classes.categoryText}>{text}</div>
      <div className={classes.categoryNum}>{num}</div>
    </div>
  );
};

const PieChart = ({ openHandler, categoryIds }) => {
  const classes = useStyles();
  const statusItems = _.map(EQUIPMENT_PIE_STATUS_ENUM, (item) => ({
    color: item.color,
    text: i18n.t(item.textKey),
    key: item.enumKey,
  }));
  const [data, setData] = useState({});
  const pieCharts = useRef();

  // pie chart 基本配置
  const tempOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}',
    },
    series: [
      {
        type: 'pie',
        radius: ['0', '90%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: 'center',
        },
        labelLine: {
          show: false,
        },
        data: [],
        itemStyle: {
          borderWidth: 2,
          borderColor: '#fff',
        },
      },
    ],
  };

  // 獲取餅圖數據 API
  const getEquipmentChart = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getEquipmentChart({ categoryIds });
      setData(result);
    } catch (error) {
      console.log(error);
    }
  }, [categoryIds]);

  useEffect(() => {
    (async () => {
      await getEquipmentChart();
    })();
  }, []);

  const options = useMemo(() => {
    let status = { tooltip: { ...tempOption.tooltip }, series: [{ ...tempOption.series[0] }] };
    let location = { tooltip: { ...tempOption.tooltip }, series: [{ ...tempOption.series[0] }] };
    let total = 0;

    if (isArray(data?.status)) {
      status.series[0].data = _.map(data.status, (item) => ({
        value: item.amount,
        name: item.name,
        itemStyle: {
          color: _.find(EQUIPMENT_PIE_STATUS_ENUM, (statusItem) => statusItem.enumKey === item.code).color,
        },
      }));
    }

    if (isArray(data?.location)) {
      location.series[0].data = _.map(data.location, (item, index) => ({
        value: item.amount,
        name: item.wardId ? item.name : i18n.t('EQUIPMENT_MANAGE_PIE_OTHER'),
        itemStyle: {
          color: _.find(EQUIPMENT_PIE_STATUS_ENUM, (statusItem) => statusItem.enumKey === index + 1).color,
        },
      }));
    }

    if (data?.total) {
      total = data.total;
    }

    return { status, location, total };
  }, [data, tempOption]);

  const _options = useMemo(
    () =>
      options.location.series[0].data.map((item, index) => ({
        key: index,
        value: item,
      })),
    [options]
  );

  const tableData = useMemo(() => {
    if (isArray(data?.location)) {
      return _.map(data.location, (item, index) => ({
        no: `${index + 1}`,
        location: item?.name,
        number: item.amount,
      }));
    } else {
      return [];
    }
  }, [data]);

  // 下載
  const downloadHandler = useCallback(async () => {
    try {
      const canvas = await html2canvas(pieCharts.current);

      canvas.toBlob(
        (callback) => {
          (async () => {
            await downloadFileById(
              () => ({
                data: callback,
              }),
              () => 'Chart'
            );
          })();
        },
        'png',
        0.8
      );
    } catch (error) {
      console.log(error);
    }
  }, [pieCharts.current]);

  const categoryItems = useMemo(() => {
    let tempCategoryItems = [];

    if (isArray(data?.category)) {
      tempCategoryItems = _.map(
        _.filter(data.category, (item) => item.amount !== 0),
        (categoryItem) => ({
          text: categoryItem.categoryName,
          num: categoryItem.amount,
        })
      );
    }

    return tempCategoryItems;
  }, [data]);

  const _categoryItems = useMemo(
    () =>
      categoryItems.map((item, index) => ({
        key: index,
        value: item,
      })),
    [categoryItems]
  );

  return (
    <Dialog className={classes.dialog} open={true}>
      <Box display="flex" alignItems="center" justifyContent="space-between" p="0 1.625rem">
        <div className={classes.h1}>{i18n.t('EQUIPMENT_MANAGE_PIE_TITLE')}</div>
        <ButtonBase id="equipment-pie-close" className={classes.closeBtn} onClick={openHandler(false)}>
          <CloseIcon className={classes.closeIcon} />
        </ButtonBase>
      </Box>
      <Box ref={pieCharts} display="flex" flexDirection="column" p="1rem 1.625rem 1.5rem">
        <Box display="flex" alignItems="center" mb="1.125rem">
          <div className={classes.h2}>{i18n.t('EQUIPMENT_MANAGE_PIE_TOTAL')}:</div>
          <div className={classes.totalEquipment}>{options.total}</div>
        </Box>
        <Box display="flex" mb="1.375rem" gridGap="0.75rem" flexWrap="wrap">
          {_categoryItems.map(({ key, value: item }) => (
            <CategoryLabel {...item} key={key} />
          ))}
        </Box>
        <Box display="flex" justifyContent="space-between" gridGap="1.438rem">
          <Box
            className={classes.pieChartBox}
            id="pieChartBox-status"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box className={classes.h2}>{i18n.t('EQUIPMENT_MANAGE_PIE_STATUS')}</Box>
            <ReactECharts option={options.status} className={classes.statusPieChart} />
            <div className={classes.statusGrid}>
              {statusItems.map((item) => (
                <StatusCom {...item} key={item.key} />
              ))}
            </div>
          </Box>
          <Box
            className={classes.pieChartBox}
            id="pieChartBox-location"
            display="flex"
            flexDirection="column"
            style={{ paddingBottom: '0' }}
          >
            <Box className={classes.h2} mb="0.5rem">
              {i18n.t('EQUIPMENT_MANAGE_PIE_LOCATION')}
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                gridRowGap="0.5rem"
                p="0.5rem 0.75rem"
                border="solid 1px #e4e7eb"
                borderRadius="8px"
                width="11rem"
                height="16.375rem"
              >
                <Box display="flex" flexDirection="column" gridRowGap="0.5rem">
                  {_options.map(({ key, value: item }) => (
                    <Box display="flex" justifyContent="space-between" key={key} alignItems="center">
                      <StatusCom text={item.name} color={item.itemStyle.color} labelFontSize="0.785rem" />
                      <div
                        className={classes.text}
                        style={{ width: '2rem', textAlign: 'center', fontSize: '0.785rem' }}
                      >
                        {item.value}
                      </div>
                    </Box>
                  ))}
                </Box>
                <div className={classes.locationRemark}>{i18n.t('EQUIPMENT_MANAGE_PIE_OTHER_PLACEHOLDER')}</div>
              </Box>
              <ReactECharts option={options.location} className={classes.locationPieChart} />
            </Box>
            <Box className={classes.h2} mt="2rem" mb="0.5rem" style={{ transform: 'translateY(-10px)' }}>
              {i18n.t('EQUIPMENT_MANAGE_PIE_ALL_TITLE')}
            </Box>
            <TableUI containerClassName={classes.containerClassName} tableClassName={classes.tableClassName}>
              <TableHead>
                <TableRow>
                  <TableCell align="left">{i18n.t('EQUIPMENT_MANAGE_PIE_TABLE_NUMBER')}</TableCell>
                  <TableCell align="center">{i18n.t('EQUIPMENT_MANAGE_PIE_TABLE_LOCATION')}</TableCell>
                  <TableCell align="center">{i18n.t('EQUIPMENT_MANAGE_PIE_TABLE_EQUIPMENT')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData.map((item) => (
                  <TableRow key={item.no}>
                    <TableCell align="left">
                      <div className={classes.text}> {item.no}</div>
                    </TableCell>
                    <TableCell align="center">
                      <div className={classes.text}> {item.location}</div>
                    </TableCell>
                    <TableCell align="center">
                      <div className={classes.text}> {item.number}</div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableUI>
          </Box>
        </Box>
      </Box>
      <Box borderBottom="1px solid rgb(230, 231, 232)" />
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" p="0.688rem 0">
        <ButtonBase
          id="equipment-pie-download"
          className={classes.downloadButton}
          onClick={() => {
            (async () => {
              await downloadHandler();
            })();
          }}
        >
          <DownloadIcon className={classes.downloadIcon} /> {i18n.t('EQUIPMENT_MANAGE_PIE_DOWNLOAD')}
        </ButtonBase>
      </Box>
    </Dialog>
  );
};

export default PieChart;
